import { Value } from '@sinclair/typebox/value';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import {
  type ListSpotlightsReply,
  type SpotlightPreviewDTO,
  type UpdateSpotlightsBody,
  UpdateSpotlightsBodySchema,
} from '../dto/spotlight.dto.js';
import { SpotlightLink } from '../models/spotlight.model.js';
import {
  buildUrlSearchParams,
  type FuuzeMutationOptions,
  type FuuzeQueryKey,
  type FuuzeQueryOptions,
  QueriesContext,
  useFuuzeQuery,
} from './shared.js';

export const spotlightQueryKeys = Object.freeze({
  list: (itemId?: string) => ['spotlights', { type: 'spotlight' }, itemId] as FuuzeQueryKey,
  preview: (url?: string) => ['spotlight-preview', { type: 'noType' }, url] as FuuzeQueryKey,
  retrieve: (urlOrId?: string) => ['spotlight-retrieve', { type: 'spotlight' }, urlOrId] as FuuzeQueryKey,
});

// =====================================================================================================================
// List
// =====================================================================================================================

export type ListSpotlights = { links: SpotlightLink[] };

const transfromListSpotlights = (dtos: ListSpotlightsReply): ListSpotlights => {
  return {
    links: dtos.links.map(dto => new SpotlightLink(dto)),
  };
};

export function useListSpotlights(itemId?: string, options?: FuuzeQueryOptions<ListSpotlightsReply, ListSpotlights>) {
  const fetchService = useContext(QueriesContext);

  return useFuuzeQuery({
    ...options,
    queryKey: spotlightQueryKeys.list(itemId),
    queryFn: () => fetchService.get<ListSpotlightsReply>(`/enrichments?itemId=${itemId}`),
    enabled: options && 'enabled' in options ? options.enabled : !!itemId,
    select: transfromListSpotlights,
    placeholderData: options?.placeholderData || { links: [] },
  });
}

// =====================================================================================================================
// Preview
// =====================================================================================================================

export function usePreviewSpotlight(url?: string, options?: FuuzeQueryOptions<SpotlightPreviewDTO>) {
  const fetchService = useContext(QueriesContext);

  return useFuuzeQuery({
    ...options,
    queryKey: spotlightQueryKeys.preview(url),
    queryFn: () => fetchService.get<SpotlightPreviewDTO>(`/enrichment/preview?${buildUrlSearchParams({ url })}`),
    enabled: options && 'enabled' in options ? options.enabled : !!url,
  });
}

// =====================================================================================================================
// Update
// =====================================================================================================================

type UpdateSpotlightsVariables = { spotlights: UpdateSpotlightsBody; itemId: string };

export function useUpdateSpotlights(options?: FuuzeMutationOptions<void, UpdateSpotlightsVariables>) {
  const fetchService = useContext(QueriesContext);
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: variables => {
      // Value.Clean output error saying the data is read-only, so I deepclone it
      return fetchService.put<void>(
        '/enrichments',
        Value.Clean(UpdateSpotlightsBodySchema, structuredClone(variables.spotlights)),
      );
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      await queryClient.invalidateQueries({ queryKey: spotlightQueryKeys.list(variables.itemId) });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
